import { Signal } from '@preact/signals-react';
import { useContext } from 'react';
import { EditorDeclarativeBlock } from '@/webapi/use-experience-api';
import { EditorContext } from '@/features/editor/context/editor-context';

import { AiComponentTab } from '@/features/editor/widgets/shared/ai-editing-layout/types';
import { useAiMetadataApi } from '@/features/ai-agents/shared/use-ai-metadata-api';
import { getQueryParam } from '@/utils/browser';
import { SelectedAgent } from '@/features/ai-agents/shared/models';

export interface ChangePersistenceActions {
  applyChanges: () => void;
  discardChanges: () => void;
  saveLocalChanges: () => void;
  discardLocalChanges: () => void;
}

export function useChangePersistence(
  newChange: Signal<EditorDeclarativeBlock>,
  lastSavedChange: Signal<EditorDeclarativeBlock>,
  hasChanges: Signal<boolean>,
  lastSavedMessageId: Signal<string>,
  selectedAgent: Signal<SelectedAgent>,
  sessionId: Signal<string>,
  selectTab: (tab: AiComponentTab) => void,
): ChangePersistenceActions {
  const {
    inspectorNav: { gotoChangelog },
    experienceState: { upsertEditorChange },
  } = useContext(EditorContext);

  const { revertSessionToLastSavedState } = useAiMetadataApi(
    getQueryParam(`local-ai`) === `1`,
  );

  const applyChanges = () => {
    upsertEditorChange(newChange.value);
    gotoChangelog();
  };

  const discardChanges = () => {
    if (lastSavedMessageId.peek() && hasChanges.peek()) {
      revertSessionToLastSavedState(
        selectedAgent.value.id,
        sessionId.value,
        lastSavedMessageId.value,
      );
    }
    gotoChangelog();
  };

  const saveLocalChanges = () => {
    lastSavedChange.value = newChange.value;
    selectTab(AiComponentTab.PROMPT);
  };

  const discardLocalChanges = () => {
    newChange.value = lastSavedChange.value;
    selectTab(AiComponentTab.PROMPT);
  };

  return {
    applyChanges,
    discardChanges,
    saveLocalChanges,
    discardLocalChanges,
  };
}
